import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Switch,
  TextField,
} from '@mui/material';
import { CommonDialog, CommonDialogHeader } from '..';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { IRolePermission, IRootState } from '../../../types/store';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';
import { NavigationService } from '../../../api/services/navigationService';
import { addNavigationPage, updateNavigationPage } from '../../../store/Reducers/NavigationSlice';
import { ICreateUpdateNavigationRequest } from '../../../types/api/request/navigationrequest.d';
import {
  ICreateUpdateNavigationDialogProps,
  ICreateUpdateNavigationDialogBodyProps,
} from '../../../types/dialog';

export const CreateUpdateNavigationDialog = ({
  selectedNavigation,
  presetParentId,
  isOpenDialog,
  setIsOpenDialog,
}: ICreateUpdateNavigationDialogProps) => {
  const dispatch = useDispatch();

  const onSubmit = async (payload: ICreateUpdateNavigationRequest) => {
    try {
      const navigation = selectedNavigation
        ? await NavigationService.updateNavigation(payload, selectedNavigation.id)
        : await NavigationService.createNavigation(payload);
      dispatch(
        selectedNavigation ? updateNavigationPage(navigation) : addNavigationPage(navigation),
      );
      dispatch(
        showNotification({
          message: `Navigation successfully ${selectedNavigation ? 'updated' : 'created'}.`,
        }),
      );
      setIsOpenDialog(false);
    } catch (error: any) {
      dispatch(
        showNotification({
          message:
            error?.response?.data?.message || error?.message || 'An unexpected error occurred.',
          severity: 'error',
        }),
      );
    }
  };

  const title = selectedNavigation
    ? 'Edit Navigation'
    : presetParentId
      ? 'Add Subpage'
      : 'Add New Navigation';

  return (
    <>
      <CommonDialog
        isOpen={isOpenDialog}
        header={
          <CommonDialogHeader
            title={title}
            onClose={() => {
              setIsOpenDialog(false);
            }}
          />
        }
        body={
          <DialogBody
            onSubmit={onSubmit}
            selectedNavigation={selectedNavigation}
            presetParentId={presetParentId}
          />
        }
        footer={undefined}
      />
    </>
  );
};

const DialogBody = ({
  onSubmit,
  selectedNavigation = null,
  presetParentId = null,
}: ICreateUpdateNavigationDialogBodyProps) => {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<ICreateUpdateNavigationRequest>({
    defaultValues: {
      title: selectedNavigation?.title ?? '',
      icon: selectedNavigation?.icon ?? '',
      route: selectedNavigation?.route ?? '',
      description: selectedNavigation?.description ?? '',
      is_active: selectedNavigation?.is_active ?? true,
      display_order: selectedNavigation?.display_order ?? 1,
      parent_id: selectedNavigation?.parent_id ?? presetParentId ?? null,
      permission_ids: selectedNavigation
        ? selectedNavigation.permissions?.map((x: IRolePermission) => x.id)
        : [],
    },
    mode: 'onChange',
  });

  const { content: navigations } = useSelector((state: IRootState) => state.navigations);
  const { content: permissions } = useSelector((state: IRootState) => state.permissions);

  // Exclude the current navigation from parent options to prevent self-referencing
  const parentOptions = navigations.filter((n) => n.id !== selectedNavigation?.id);

  const buttonLabel = selectedNavigation ? 'Update Navigation' : 'Create Navigation';
  const loadingLabel = selectedNavigation ? 'Updating...' : 'Creating...';

  const handleFormSubmit = async (data: ICreateUpdateNavigationRequest) => {
    try {
      await onSubmit(data);
      reset();
    } catch {}
  };

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit(handleFormSubmit)}
        noValidate
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          gap: 2,
        }}
      >
        {/* Title */}
        <FormControl>
          <FormLabel>Title</FormLabel>
          <TextField
            type="text"
            placeholder="e.g. New Arrivals"
            fullWidth
            variant="outlined"
            error={!!errors.title}
            helperText={errors.title?.message}
            {...register('title', {
              required: 'Title is required',
            })}
          />
        </FormControl>

        {/* Icon */}
        <FormControl>
          <FormLabel>Icon</FormLabel>
          <TextField
            type="text"
            placeholder="e.g. home-icon"
            fullWidth
            variant="outlined"
            error={!!errors.icon}
            helperText={errors.icon?.message}
            {...register('icon')}
          />
        </FormControl>

        {/* Active */}
        <FormControl>
          <Controller
            name="is_active"
            control={control}
            render={({ field: { onChange, value } }) => (
              <FormControlLabel
                control={<Switch checked={value} onChange={(_, checked) => onChange(checked)} />}
                label="Active"
              />
            )}
          />
        </FormControl>

        {/* Route */}
        <FormControl>
          <FormLabel>Route</FormLabel>
          <TextField
            type="text"
            placeholder="e.g. /product/newarrival"
            fullWidth
            variant="outlined"
            error={!!errors.route}
            helperText={errors.route?.message}
            {...register('route', {
              required: 'Route is required',
              pattern: {
                value: /^\//,
                message: 'Route must start with /',
              },
            })}
          />
        </FormControl>

        {/* Description */}
        <FormControl>
          <FormLabel>Description</FormLabel>
          <TextField
            type="text"
            placeholder="e.g. Select items for the new arrivals section"
            fullWidth
            variant="outlined"
            error={!!errors.description}
            helperText={errors.description?.message}
            {...register('description', {
              required: 'Description is required',
            })}
          />
        </FormControl>

        {/* Display Order */}
        <FormControl>
          <FormLabel>Display Order</FormLabel>
          <TextField
            type="number"
            placeholder="e.g. 1"
            fullWidth
            variant="outlined"
            error={!!errors.display_order}
            helperText={errors.display_order?.message}
            {...register('display_order', {
              required: 'Display order is required',
              valueAsNumber: true,
              min: { value: 1, message: 'Display order must be at least 1' },
            })}
          />
        </FormControl>

        {/* Parent Navigation */}
        <FormControl>
          <FormLabel>Parent Navigation</FormLabel>
          <Controller
            name="parent_id"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                options={parentOptions}
                getOptionLabel={(option) => option.title}
                value={parentOptions.find((n) => n.id === value) ?? null}
                onChange={(_, selectedOption) => onChange(selectedOption?.id ?? null)}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select parent navigation (optional)" />
                )}
              />
            )}
          />
        </FormControl>

        {/* Permissions */}
        <FormControl>
          <FormLabel>Permissions</FormLabel>
          <Controller
            name="permission_ids"
            control={control}
            rules={{ required: 'Please select at least one permission' }}
            render={({ field: { onChange, value } }) => (
              <Autocomplete
                multiple
                options={permissions || []}
                disableCloseOnSelect
                getOptionLabel={(option) => option.name}
                value={permissions?.filter((p) => value?.includes(p.id)) || []}
                onChange={(_, selectedOptions) =>
                  onChange(selectedOptions.map((option) => option.id))
                }
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox style={{ marginRight: 8 }} checked={selected} />
                    {option.name}
                  </li>
                )}
                renderInput={(params) => <TextField {...params} placeholder="Select permissions" />}
              />
            )}
          />
          {errors.permission_ids && (
            <Box sx={{ color: 'red', mt: 0.5 }}>{errors.permission_ids.message}</Box>
          )}
        </FormControl>

        {/* Submit */}
        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting}
          sx={{ backgroundColor: '#000' }}
        >
          {isSubmitting ? loadingLabel : buttonLabel}
        </Button>
      </Box>
    </>
  );
};
